import type {
  CreateDuelPayload,
  DuelDistancePayload,
  DuelDto,
  DuelMoveDto,
  DuelPingPayload,
  DuelRemovalDto,
  MakeDuelMovePayload,
} from '@/core/data/duel/duel.interface';
import { DuelEndpoint } from '@/core/network/api/constants/api.url.constant';
import { HttpClient } from '@/core/network/http/httpClient';
import type { GamResponse } from '@/core/network/http/httpClient.interface';
import type { AxiosResponse } from 'axios';

export class DuelApi {
  private readonly httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  async create(payload: CreateDuelPayload): Promise<AxiosResponse<GamResponse<DuelDto>>> {
    return this.httpClient.post<DuelDto, CreateDuelPayload>(DuelEndpoint.CREATE, {
      data: payload,
    });
  }

  async join(code: string): Promise<AxiosResponse<GamResponse<DuelDto>>> {
    return this.httpClient.post<DuelDto, { code: string }>(DuelEndpoint.JOIN, {
      data: { code },
    });
  }

  async getActive(): Promise<AxiosResponse<GamResponse<DuelDto | null>>> {
    return this.httpClient.get<DuelDto | null>(DuelEndpoint.ACTIVE);
  }

  async getMine(limit?: number): Promise<AxiosResponse<GamResponse<DuelDto[]>>> {
    return this.httpClient.get<DuelDto[]>(DuelEndpoint.MINE, {
      queryParams: limit ? { limit: String(limit) } : undefined,
    });
  }

  async getByCode(code: string): Promise<AxiosResponse<GamResponse<DuelDto>>> {
    return this.httpClient.get<DuelDto>(DuelEndpoint.GET_BY_CODE, {
      urlParams: { code },
    });
  }

  async getDuel(id: string): Promise<AxiosResponse<GamResponse<DuelDto>>> {
    return this.httpClient.get<DuelDto>(DuelEndpoint.GET_DUEL, {
      urlParams: { id },
    });
  }

  async getMoves(id: string): Promise<AxiosResponse<GamResponse<DuelMoveDto[]>>> {
    return this.httpClient.get<DuelMoveDto[]>(DuelEndpoint.GET_MOVES, {
      urlParams: { id },
    });
  }

  async ready(id: string, speedMps: number): Promise<AxiosResponse<GamResponse<DuelDto>>> {
    return this.httpClient.post<DuelDto, { speedMps: number }>(DuelEndpoint.READY, {
      urlParams: { id },
      data: { speedMps },
    });
  }

  async start(id: string): Promise<AxiosResponse<GamResponse<DuelDto>>> {
    return this.httpClient.post<DuelDto, undefined>(DuelEndpoint.START, {
      urlParams: { id },
    });
  }

  async makeMove(id: string, payload: MakeDuelMovePayload): Promise<AxiosResponse<GamResponse<DuelDto>>> {
    return this.httpClient.post<DuelDto, MakeDuelMovePayload>(DuelEndpoint.MAKE_MOVE, {
      urlParams: { id },
      data: payload,
    });
  }

  async ping(id: string, payload: DuelPingPayload): Promise<AxiosResponse<GamResponse<DuelDto>>> {
    return this.httpClient.post<DuelDto, DuelPingPayload>(DuelEndpoint.PING, {
      urlParams: { id },
      data: payload,
    });
  }

  async cancel(id: string): Promise<AxiosResponse<GamResponse<DuelDto>>> {
    return this.httpClient.post<DuelDto, undefined>(DuelEndpoint.CANCEL, {
      urlParams: { id },
    });
  }

  async resign(id: string, payload: DuelDistancePayload): Promise<AxiosResponse<GamResponse<DuelDto>>> {
    return this.httpClient.post<DuelDto, DuelDistancePayload>(DuelEndpoint.RESIGN, {
      urlParams: { id },
      data: payload,
    });
  }

  async offerDraw(id: string): Promise<AxiosResponse<GamResponse<DuelDto>>> {
    return this.httpClient.post<DuelDto, undefined>(DuelEndpoint.OFFER_DRAW, {
      urlParams: { id },
    });
  }

  async acceptDraw(id: string, payload: DuelDistancePayload): Promise<AxiosResponse<GamResponse<DuelDto>>> {
    return this.httpClient.post<DuelDto, DuelDistancePayload>(DuelEndpoint.ACCEPT_DRAW, {
      urlParams: { id },
      data: payload,
    });
  }

  async declineDraw(id: string): Promise<AxiosResponse<GamResponse<DuelDto>>> {
    return this.httpClient.post<DuelDto, undefined>(DuelEndpoint.DECLINE_DRAW, {
      urlParams: { id },
    });
  }

  async removeMe(id: string): Promise<AxiosResponse<GamResponse<DuelRemovalDto>>> {
    return this.httpClient.delete<DuelRemovalDto, undefined>(DuelEndpoint.REMOVE_ME, {
      urlParams: { id },
    });
  }
}
